// Utility functions for subscription management
import { supabase } from '../supabaseClient';

/**
 * Check if user has active Pro access (including cancelled but still valid)
 * @param {Object} user - User object from AuthContext
 * @returns {boolean} - True if user has Pro access
 */
export const hasProAccess = (user) => {
  if (!user) return false;
  const status = user.subscription_status || user.subscription;
  return status === 'active' || status === 'cancelled_retain_access';
};

/**
 * Check if user can export PowerShell (Pro users unlimited, free users 1 per month)
 * @param {Object} user - User object from AuthContext
 * @returns {Promise<boolean>} - True if user can export
 */
export const canExportPowerShell = async (user) => {
  if (!user) return false;
  
  // Pro users have unlimited exports
  if (hasProAccess(user)) {
    return true;
  }
  
  try {
    const { data, error } = await supabase.rpc('check_monthly_export_limit', {
      user_id: user.id
    });
    
    if (error) {
      console.error('Error checking export limit:', error);
      return false;
    }
    
    return data;
  } catch (error) {
    console.error('Error checking export limit:', error);
    return false;
  }
};

/**
 * Get monthly export status for free users
 * @param {Object} user - User object from AuthContext
 * @returns {Promise<Object>} - Export status object
 */
export const getMonthlyExportStatus = async (user) => {
  if (!user) {
    return {
      can_export: false,
      exports_used: 0,
      exports_remaining: 0,
      reset_date: null,
      is_pro: false,
      error: 'User not authenticated'
    };
  }
  
  // Pro users have unlimited exports
  if (hasProAccess(user)) {
    return {
      can_export: true,
      exports_used: 0,
      exports_remaining: -1, // -1 indicates unlimited
      reset_date: null,
      is_pro: true
    };
  }
  
  try {
    const { data, error } = await supabase.rpc('get_monthly_export_status', {
      user_id: user.id
    });
    
    if (error) {
      console.error('Error getting export status:', error);
      return {
        can_export: false,
        exports_used: 0,
        exports_remaining: 0,
        reset_date: null,
        is_pro: false,
        error: 'Failed to get export status'
      };
    }
    
    return data;
  } catch (error) {
    console.error('Error getting export status:', error);
    return {
      can_export: false,
      exports_used: 0,
      exports_remaining: 0,
      reset_date: null,
      is_pro: false,
      error: 'Failed to get export status'
    };
  }
};

/**
 * Increment monthly export count for free users
 * @param {Object} user - User object from AuthContext
 * @returns {Promise<boolean>} - True if successfully incremented
 */
export const incrementMonthlyExportCount = async (user) => {
  if (!user) return false;
  
  // Pro users don't need to track exports
  if (hasProAccess(user)) {
    return true;
  }
  
  try {
    const { error } = await supabase.rpc('increment_monthly_export_count', {
      user_id: user.id
    });
    
    if (error) {
      console.error('Error incrementing export count:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error incrementing export count:', error);
    return false;
  }
};

/**
 * Calculate remaining days for cancelled subscriptions
 * @param {string} endDate - Subscription end date
 * @returns {number|null} - Number of days remaining, or null if no end date
 */
export const getRemainingDays = (endDate) => {
  if (!endDate) return null;
  const now = new Date();
  const end = new Date(endDate);
  const diffTime = end - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

/**
 * Get subscription display information
 * @param {Object} user - User object from AuthContext
 * @returns {Object} - Object with status, color, description, and warning info
 */
export const getSubscriptionDisplay = (user) => {
  if (!user) return { status: 'Free', color: 'text-indigo-700' };
  
  const status = user.subscription_status || user.subscription;
  const endDate = user.subscription_end_date;
  
  switch (status) {
    case 'active':
      return { 
        status: 'Pro (Active)', 
        color: 'text-green-700',
        description: 'Your Pro subscription is active.'
      };
    case 'cancelled_retain_access':
      const remainingDays = getRemainingDays(endDate);
      return {
        status: `Pro (Cancelled - ${remainingDays} days remaining)`,
        color: 'text-orange-600',
        description: `Your subscription has been cancelled but you retain Pro access until ${new Date(endDate).toLocaleDateString()}.`,
        warning: true,
        remainingDays
      };
    case 'past_due':
      return {
        status: 'Pro (Payment Past Due)',
        color: 'text-red-600',
        description: 'Your payment is past due. Please update your payment method to continue Pro access.'
      };
    case 'unpaid':
      return {
        status: 'Pro (Payment Failed)',
        color: 'text-red-600',
        description: 'Your payment failed. Please update your payment method to restore Pro access.'
      };
    case 'trial_ending':
      return {
        status: 'Pro (Trial Ending)',
        color: 'text-orange-600',
        description: 'Your trial is ending soon. Please add a payment method to continue Pro access.'
      };
    default:
      return { 
        status: 'Free', 
        color: 'text-indigo-700',
        description: 'Free plan includes 1 PowerShell export per month. Upgrade to Pro for unlimited exports.'
      };
  }
};

/**
 * Check if subscription is in a warning state (cancelled but still active)
 * @param {Object} user - User object from AuthContext
 * @returns {boolean} - True if subscription needs attention
 */
export const isSubscriptionWarning = (user) => {
  if (!user) return false;
  const status = user.subscription_status || user.subscription;
  return status === 'cancelled_retain_access' || status === 'past_due' || status === 'unpaid' || status === 'trial_ending';
};

/**
 * Get subscription status for API calls
 * @param {Object} user - User object from AuthContext
 * @returns {string} - Subscription status for backend
 */
export const getSubscriptionStatus = (user) => {
  if (!user) return 'free';
  const status = user.subscription_status || user.subscription;
  return status === 'active' || status === 'cancelled_retain_access' ? 'paid' : 'free';
}; 